import React, { useState, useEffect } from 'react';
import './GeofenceManager.css';
import { API_BASE } from '../../config';

const ZONE_TYPES = [
  { value: 'segment_zone', dbValue: 'segment', label: 'Segment Zone', color: '#8b5cf6' },
  { value: 'restricted', dbValue: 'restricted', label: 'Restricted', color: '#ef4444' },
  { value: 'constrained_corridor', dbValue: 'corridor', label: 'Constrained Corridor', color: '#3b82f6' }
];

export const GeofenceManager = ({ onGeofencesLoad, onDrawModeChange, isOpen, onClose, refreshKey = 0 }) => {
  const [geofences, setGeofences] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedZoneType, setSelectedZoneType] = useState('segment_zone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPanel, setShowPanel] = useState(isOpen !== undefined ? isOpen : false);
  const [isDrawSectionCollapsed, setIsDrawSectionCollapsed] = useState(false);
  const [isListSectionCollapsed, setIsListSectionCollapsed] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editZoneType, setEditZoneType] = useState('');

  useEffect(() => {
    fetchGeofences();
  }, []);

  // Re-fetch when parent signals a new geofence was saved
  useEffect(() => {
    if (refreshKey > 0) {
      fetchGeofences();
    }
  }, [refreshKey]);

  useEffect(() => {
    if (isOpen !== undefined) {
      setShowPanel(isOpen);
    }
  }, [isOpen]);

  // Turn off drawing when panel closes
  useEffect(() => {
    if (!showPanel && isDrawing) {
      setIsDrawing(false);
      if (onDrawModeChange) {
        onDrawModeChange(false, selectedZoneType);
      }
    }
  }, [showPanel]);

  const fetchGeofences = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/geofences`);
      if (!response.ok) throw new Error('Failed to fetch geofences');
      
      const data = await response.json();
      setGeofences(data.features || []);
      
      if (onGeofencesLoad) {
        onGeofencesLoad(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDrawing = () => {
    const newDrawingState = !isDrawing;
    setIsDrawing(newDrawingState);
    
    if (onDrawModeChange) {
      onDrawModeChange(newDrawingState, selectedZoneType);
    }
  };

  const handleZoneTypeChange = (zoneType) => {
    setSelectedZoneType(zoneType);
    
    if (isDrawing && onDrawModeChange) {
      onDrawModeChange(true, zoneType);
    }
  };

  const handleDeleteGeofence = async (geofenceId) => {
    if (!window.confirm('Are you sure you want to delete this zone?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/geofences/${geofenceId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete zone');
      
      await fetchGeofences();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStartEdit = (geofence) => {
    const props = geofence.properties || {};
    setEditingGeofence(props.id || geofence.id);
    setEditName(props.name || '');
    setEditDescription(props.description || '');
    setEditZoneType(props.zone_type || '');
  };

  const handleCancelEdit = () => {
    setEditingGeofence(null);
    setEditName('');
    setEditDescription('');
    setEditZoneType('');
  };

  const handleSaveEdit = async (geofenceId) => {
    try {
      const response = await fetch(`${API_BASE}/api/geofences/${geofenceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editName,
          description: editDescription,
          zone_type: editZoneType,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update zone');
      }

      setEditingGeofence(null);
      setEditName('');
      setEditDescription('');
      setEditZoneType('');
      await fetchGeofences();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleClosePanel = () => {
    // Turn off drawing when closing
    if (isDrawing) {
      setIsDrawing(false);
      if (onDrawModeChange) {
        onDrawModeChange(false, selectedZoneType);
      }
    }
    setEditingGeofence(null);
    setShowPanel(false);
    if (onClose) {
      onClose();
    }
  };

  const getZoneTypeColor = (zoneType) => {
    const type = ZONE_TYPES.find(t => t.value === zoneType || t.dbValue === zoneType);
    return type ? type.color : '#6b7280';
  };

  const getZoneTypeLabel = (zoneType) => {
    const type = ZONE_TYPES.find(t => t.value === zoneType || t.dbValue === zoneType);
    return type ? type.label : zoneType;
  };

  const selectedTypeInfo = ZONE_TYPES.find(t => t.value === selectedZoneType) || ZONE_TYPES[0];

  return (
    <>
      {isOpen === undefined && (
        <button
          className="geofence-toggle-button"
          onClick={() => setShowPanel(!showPanel)}
          title="Geofence Manager"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="10" r="3" strokeWidth="2"/>
          </svg>
        </button>
      )}

      {showPanel && (
        <div className="geofence-manager">
          <div className="geofence-manager-header">
            <h3>Zone Manager</h3>
            <button 
              onClick={handleClosePanel} 
              className="close-button"
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          {/* Draw Section */}
          <div className="geofence-section">
            <div 
              className="geofence-section-header"
              onClick={() => setIsDrawSectionCollapsed(!isDrawSectionCollapsed)}
            >
              <h4>Draw New Zone</h4>
              <button 
                className="collapse-button"
                aria-label={isDrawSectionCollapsed ? "Expand" : "Collapse"}
              >
                <svg 
                  width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
                  style={{ transform: isDrawSectionCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                >
                  <path d="M3 4.5L6 7.5L9 4.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {!isDrawSectionCollapsed && (
              <div className="geofence-draw-controls">
                <div className="zone-type-selector">
                  {ZONE_TYPES.map(type => (
                    <button
                      key={type.value}
                      className={`zone-type-button ${selectedZoneType === type.value ? 'active' : ''}`}
                      style={{ 
                        borderColor: selectedZoneType === type.value ? type.color : undefined,
                        backgroundColor: selectedZoneType === type.value ? `${type.color}20` : 'transparent'
                      }}
                      onClick={() => handleZoneTypeChange(type.value)}
                    >
                      <span className="zone-color-dot" style={{ backgroundColor: type.color }} />
                      {type.label}
                    </button>
                  ))}
                </div>

                <button
                  className={`draw-toggle-button ${isDrawing ? 'active' : ''}`}
                  onClick={handleToggleDrawing}
                  style={isDrawing ? { backgroundColor: selectedTypeInfo.color, borderColor: selectedTypeInfo.color } : {}}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {isDrawing ? (
                      <rect x="6" y="6" width="12" height="12" fill="currentColor" stroke="none"/>
                    ) : (
                      <>
                        <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="12" y1="22" x2="12" y2="15.5"/>
                        <polyline points="22 8.5 12 15.5 2 8.5"/>
                      </>
                    )}
                  </svg>
                  {isDrawing ? 'Stop Drawing' : 'Start Drawing'}
                </button>

                {isDrawing && (
                  <div className="draw-instructions" style={{ borderColor: selectedTypeInfo.color }}>
                    <div className="draw-instructions-header">
                      <span className="draw-status-dot" style={{ backgroundColor: selectedTypeInfo.color }}></span>
                      <strong>Drawing {selectedTypeInfo.label}</strong>
                    </div>
                    <ol className="draw-steps">
                      <li>Click on the map to place polygon vertices</li>
                      <li>Click the first point again to close the polygon</li>
                      <li>You'll be prompted to name the zone</li>
                    </ol>
                    <div className="draw-hint">
                      {selectedZoneType === 'segment_zone' && 'Segment zones define where vessel runs start and end.'}
                      {selectedZoneType === 'restricted' && 'Restricted zones flag violations when a vessel enters.'}
                      {selectedZoneType === 'constrained_corridor' && 'Constrained corridors enforce lateral vessel limits and optional time windows.'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* List Section */}
          <div className="geofence-section geofence-list-section">
            <div 
              className="geofence-section-header"
              onClick={() => setIsListSectionCollapsed(!isListSectionCollapsed)}
            >
              <h4>Existing Zones ({geofences.length})</h4>
              <button 
                className="collapse-button"
                aria-label={isListSectionCollapsed ? "Expand" : "Collapse"}
              >
                <svg 
                  width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
                  style={{ transform: isListSectionCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                >
                  <path d="M3 4.5L6 7.5L9 4.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {!isListSectionCollapsed && (
              <div className="geofence-list">
                {loading && <div className="loading">Loading zones...</div>}
                {error && <div className="error">{error}</div>}
                
                {!loading && geofences.length === 0 && (
                  <div className="empty-state">No zones created yet</div>
                )}

                {geofences.map(geofence => {
                  const geofenceId = geofence.properties?.id || geofence.id;
                  const isEditing = editingGeofence === geofenceId;

                  return (
                    <div key={geofenceId} className="geofence-item">
                      {isEditing ? (
                        <div className="geofence-edit-form">
                          <div className="edit-field">
                            <label className="edit-label">Name</label>
                            <input
                              type="text"
                              className="edit-input"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              autoFocus
                            />
                          </div>
                          <div className="edit-field">
                            <label className="edit-label">Description</label>
                            <input
                              type="text"
                              className="edit-input"
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              placeholder="Optional description"
                            />
                          </div>
                          <div className="edit-field">
                            <label className="edit-label">Type</label>
                            <select
                              className="edit-select"
                              value={editZoneType}
                              onChange={(e) => setEditZoneType(e.target.value)}
                            >
                              {ZONE_TYPES.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="geofence-actions">
                            <button
                              className="cancel-edit-button"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>
                            <button
                              className="save-edit-button"
                              onClick={() => handleSaveEdit(geofenceId)}
                              disabled={!editName.trim()}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="geofence-item-header">
                            <span 
                              className="zone-type-badge"
                              style={{ backgroundColor: getZoneTypeColor(geofence.properties?.zone_type) }}
                            >
                              {getZoneTypeLabel(geofence.properties?.zone_type)}
                            </span>
                            <span className="geofence-name">{geofence.properties?.name}</span>
                          </div>
                          
                          {geofence.properties?.description && (
                            <div className="geofence-description">
                              {geofence.properties.description}
                            </div>
                          )}
                          
                          <div className="geofence-actions">
                            <button
                              className="edit-button"
                              onClick={() => handleStartEdit(geofence)}
                              title="Edit zone"
                            >
                              Edit
                            </button>
                            <button
                              className="delete-button"
                              onClick={() => handleDeleteGeofence(geofenceId)}
                              title="Delete zone"
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default GeofenceManager;